import { useEffect, useId, useRef, useState } from "react";
import { ShieldCheck, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { GlyphTile } from "@/components/icons/GlyphTile";
import LegalLink, { LEGAL_URLS } from "@/components/LegalLink";
import ConfirmDialog from "@/components/ConfirmDialog";
import { accountDeletionBody, consentWithdrawalBody } from "@/lib/legalCopy";
import { updateProfile, signOut, deleteAccount, fetchSubscription, cancelSubscription } from "@/lib/api";
import { useModalFocus } from "../hooks/useModalFocus";

// One-time Kullanım Şartları (re-)acceptance, shown by pages/Luna.jsx when
// the profile's terms_accepted_version !== terms_current_version — i.e. every
// account that predates in-app acceptance, one created via Google (no signup
// form to tick anything on), or anyone after the terms version changes.
// Deliberately NOT dismissible (no overlay click / Escape / close button):
// the ways out are accepting, signing out — or, for someone who doesn't
// accept the (new) terms, deleting the account / cancelling a paid
// subscription right here, since the gate hides Settings and Luna Premium
// (cancelling must never be harder than signing up).
export default function TermsGate({ lang, currentVersion, initialConsent = false, onAccepted }) {
  const t = (tr, en) => (lang === "tr" ? tr : en);
  const [termsChecked, setTermsChecked] = useState(false);
  // Pre-filled from the account's CURRENT choice, so re-accepting updated
  // terms never silently withdraws a consent the user had already given.
  const [consentChecked, setConsentChecked] = useState(!!initialConsent);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // "account" | "cancel" | null
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [accountDeleted, setAccountDeleted] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const dialogRef = useRef(null);
  const titleId = useId();
  const termsId = useId();
  const consentId = useId();

  // The gate takes the focus, and Tab stays inside it: from the keyboard
  // the app behind it can't be reached either (audit client-09). While one
  // of its confirm dialogs is open, that dialog has the keyboard.
  useModalFocus(dialogRef, { trap: !confirming });

  useEffect(() => {
    fetchSubscription()
      .then((d) => {
        const s = d?.subscription;
        setHasPaidSubscription(s?.status === "active" && !!s?.plan && s.plan !== "free");
      })
      .catch(() => {});
  }, []);

  const confirmDeleteAccount = async () => {
    if (deletingAccount) return;
    setDeletingAccount(true);
    try {
      // deleteAccount() also signs this browser out — only redirect after it.
      await deleteAccount();
      setAccountDeleted(true);
      toast.success(t("Hesabın ve tüm verilerin silindi. Hoşça kal 💜", "Your account and all its data were deleted. Goodbye 💜"));
      setTimeout(() => { window.location.href = "/"; }, 1800);
    } catch (e) {
      setDeletingAccount(false);
      toast.error(e?.response?.data?.detail || t("Hesabın silinemedi, tekrar dene.", "Couldn't delete your account, try again."));
    }
  };

  const confirmCancelSubscription = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await cancelSubscription();
      setHasPaidSubscription(false);
      setConfirming(null);
      toast.success(t("Aboneliğin iptal edildi. Kartından yeni ödeme alınmayacak.", "Your subscription was cancelled. Your card won't be charged again."));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Abonelik iptal edilemedi, tekrar dene.", "Couldn't cancel the subscription, try again."));
    } finally {
      setCancelling(false);
    }
  };

  const accept = async () => {
    if (!termsChecked || busy) return;
    setBusy(true);
    try {
      await updateProfile({ accept_terms_version: currentVersion, special_data_consent: consentChecked });
      onAccepted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Onayın kaydedilemedi, tekrar dene.", "Couldn't save your acceptance, try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = () => {
    signOut();
    window.location.href = "/";
  };

  const summary = [
    t("Luna'nın cevaplarını yapay zeka üretir; hatalı olabilir ve tıbbi, hukuki, mali veya psikolojik profesyonel tavsiye yerine geçmez.",
      "Luna's replies are generated by AI; they may be wrong and are not a substitute for professional medical, legal, financial or psychological advice."),
    t("Kurallara aykırı kullanımda hesabın askıya alınabilir veya kapatılabilir.",
      "Your account may be suspended or closed if you break the rules."),
    t("Luna 18 yaşından büyükler içindir.", "Luna is for adults 18 and over."),
    t("Ücretli paketler aylık yenilenir; aboneliğini uygulamadan dilediğin zaman iptal edebilirsin.",
      "Paid plans renew monthly; you can cancel your subscription in the app at any time."),
  ];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4"
      data-testid="terms-gate">
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
        className="w-full max-w-md max-h-full flex flex-col rounded-3xl border border-purple-400/20 overflow-hidden outline-none"
        style={{ backgroundColor: "#0c0818", boxShadow: "0 20px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
        {/* Scrollable body — the action buttons below stay visible even on a
            360px-wide phone where all of this doesn't fit at once. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-5 pb-4 sm:px-6 sm:pt-6">
          {/* Tile + title/subtitle column, like the panel headers. */}
          <div className="flex items-start gap-3 mb-4">
            <GlyphTile icon={ShieldCheck} hue="violet" size={40} />
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id={titleId} className="text-base font-bold text-white leading-tight">
                {t("Devam etmeden önce", "Before you continue")}
              </h2>
              <p className="mt-1 text-xs text-white/50">
                {t("Luna'yı kullanmaya devam etmeden önce bu kısa özeti oku.", "Please read this short summary before you keep using Luna.")}
              </p>
            </div>
          </div>

          <ul className="rounded-2xl p-4 mb-4 space-y-2 border border-purple-400/25"
            style={{ backgroundColor: "rgba(192,132,252,0.08)" }} data-testid="terms-gate-summary">
            {summary.map((line) => (
              <li key={line} className="flex gap-2 text-xs text-white/85 leading-relaxed">
                <span className="text-purple-300 shrink-0" aria-hidden="true">•</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-start gap-2.5 mb-3">
            <input id={termsId} type="checkbox" checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)}
              data-testid="terms-gate-terms-checkbox"
              className="accent-purple-400 mt-0.5 h-4 w-4 shrink-0 cursor-pointer" />
            <label htmlFor={termsId} className="text-xs text-white/85 leading-relaxed cursor-pointer">
              {t(
                <>18 yaşını doldurdum; <LegalLink href={LEGAL_URLS.terms} className="text-purple-300 hover:text-purple-200">Kullanım Şartları</LegalLink>'nı okudum ve kabul ediyorum.</>,
                <>I am 18 or older, and I have read and accept the <LegalLink href={LEGAL_URLS.terms} className="text-purple-300 hover:text-purple-200">Terms of Use</LegalLink>.</>
              )}
            </label>
          </div>

          <p className="text-[11px] text-white/45 leading-relaxed mb-5 pl-[26px]">
            {t(
              <>Kişisel verilerinin nasıl işlendiğini <LegalLink href={LEGAL_URLS.kvkk}>KVKK Aydınlatma Metni</LegalLink>'nde ve <LegalLink href={LEGAL_URLS.privacy}>Gizlilik Politikası</LegalLink>'nda okuyabilirsin.</>,
              <>You can read how your personal data is processed in the <LegalLink href={LEGAL_URLS.kvkk}>KVKK Privacy Notice</LegalLink> and the <LegalLink href={LEGAL_URLS.privacy}>Privacy Policy</LegalLink>.</>
            )}
          </p>

          <div className="pt-4 border-t border-white/10">
            {/* lang: CSS `uppercase` follows the element's language — under the
                page's lang="tr" the EN text would become "EXPLİCİT". */}
            <h3 lang={lang === "tr" ? "tr" : "en"} className="text-[11px] font-semibold uppercase tracking-wide text-purple-300/60 mb-2.5">
              {t("Açık rıza (isteğe bağlı)", "Explicit consent (optional)")}
            </h3>
            <div className="flex items-start gap-2.5">
              <input id={consentId} type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)}
                data-testid="terms-gate-consent-checkbox"
                className="accent-purple-400 mt-0.5 h-4 w-4 shrink-0 cursor-pointer" />
              <label htmlFor={consentId} className="text-xs text-white/75 leading-relaxed cursor-pointer">
                {t(
                  <>Sohbetlerimde paylaştığım sağlık, inanç gibi özel nitelikli kişisel verilerimin <LegalLink href={LEGAL_URLS.consent} className="text-purple-300 hover:text-purple-200">Açık Rıza Metni</LegalLink>'nde anlatıldığı şekilde işlenmesine ve Luna'nın bunları hafızasına kaydetmesine açık rıza veriyorum.</>,
                  <>I give my explicit consent for the special-category personal data I share in my chats, such as health or beliefs, to be processed as described in the <LegalLink href={LEGAL_URLS.consent} className="text-purple-300 hover:text-purple-200">Explicit Consent Notice</LegalLink>, and for Luna to save it to its memory.</>
                )}
              </label>
            </div>
            <p className="text-[11px] text-white/40 leading-relaxed mt-2 pl-[26px]">
              {t(
                "Vermesen de Luna'yı kullanabilirsin; bu durumda Luna bu tür bilgileri hafızasına ve özetlerine kaydetmez. Kararını Ayarlar > Gizlilik ve Veri'den istediğin zaman değiştirebilirsin.",
                "You can use Luna without it; in that case Luna won't save this kind of information to its memory or summaries. You can change your decision anytime in Settings > Privacy & Data."
              )}
            </p>
            {initialConsent && !consentChecked && (
              <p className="text-[11px] text-red-300/80 leading-relaxed mt-2 pl-[26px]" data-testid="terms-gate-consent-withdraw-note">
                {consentWithdrawalBody(t)}
              </p>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-white/10 px-5 py-4 sm:px-6 flex flex-col gap-2">
          <button type="button" onClick={accept} disabled={!termsChecked || busy} data-testid="terms-gate-accept-button"
            className="w-full flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ background: "linear-gradient(90deg,#6366f1,#c084fc)" }}>
            {busy && <Loader2 size={15} className="animate-spin" />}
            {t("Kabul et ve devam et", "Accept and continue")}
          </button>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <button type="button" onClick={handleSignOut} disabled={busy} data-testid="terms-gate-sign-out"
              className="flex items-center justify-center gap-1.5 text-xs font-semibold text-white/40 hover:text-white transition-colors py-2 disabled:opacity-40">
              <LogOut size={14} /> {t("Çıkış yap", "Sign out")}
            </button>
            {hasPaidSubscription && (
              <button type="button" onClick={() => setConfirming("cancel")} disabled={busy} data-testid="terms-gate-cancel-subscription"
                className="text-xs font-semibold text-white/40 hover:text-white transition-colors py-2 disabled:opacity-40">
                {t("Aboneliği iptal et", "Cancel subscription")}
              </button>
            )}
            <button type="button" onClick={() => setConfirming("account")} disabled={busy} data-testid="terms-gate-delete-account"
              className="text-xs font-semibold text-red-300/60 hover:text-red-200 transition-colors py-2 disabled:opacity-40">
              {t("Hesabımı sil", "Delete my account")}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirming === "cancel"}
        danger
        busy={cancelling}
        title={t("Aboneliğini iptal etmek istiyor musun?", "Cancel your subscription?")}
        body={t(
          "İptal ettiğinde paketin hemen sona erer, hesabın ücretsiz pakete geçer ve kartından yeni bir ödeme alınmaz.",
          "When you cancel, your plan ends right away, your account moves to the free plan and your card won't be charged again."
        )}
        confirmLabel={t("Aboneliği iptal et", "Cancel subscription")}
        cancelLabel={t("Vazgeç", "Keep it")}
        onConfirm={confirmCancelSubscription}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming === "account"}
        danger
        busy={deletingAccount}
        title={t("Hesabını silmek istiyor musun?", "Delete your account?")}
        body={accountDeletionBody(t)}
        confirmLabel={accountDeleted
          ? t("Hesabın silindi, yönlendiriliyorsun...", "Account deleted, redirecting...")
          : t("Hesabımı kalıcı olarak sil", "Permanently delete my account")}
        cancelLabel={t("Vazgeç", "Cancel")}
        onConfirm={confirmDeleteAccount}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
